import type { CSSProperties } from "react";

import { cn } from "../../lib/utils.js";
import { festiveTooltip, type ActiveFestival } from "../../lib/festive/getActiveFestival.js";
import { useFestiveReducedMotion } from "../../hooks/useActiveFestival.js";
import { festiveIcon, isCompanyIcon } from "./festive-icons.js";

interface FestiveBadgeProps {
  active: ActiveFestival;
  /**
   * "logo" (default): a circle over the top-right corner of the logo. The caller sizes and places
   * it through `className`, using percentages of the logo's box.
   * "name": a bare icon 12px to the right of the wordmark. The caller sizes it through `className`.
   */
  placement?: "logo" | "name";
  className?: string;
}

/**
 * The festive icon. The wrapper is absolutely positioned to fill the element it sits in (the logo
 * slot or tile, which must be position: relative), so the logo itself never moves or resizes.
 * The badge is a focusable role="img" with the full text as its label. Its tooltip shows on hover
 * and on keyboard focus.
 */
export function FestiveBadge({ active, placement = "logo", className }: FestiveBadgeProps) {
  const reduce = useFestiveReducedMotion();
  const tip = festiveTooltip(active);
  const { icon, accent } = active.festival;
  const company = isCompanyIcon(icon);

  // The event colour is a config value, so it reaches the company icon through a custom property
  // on this root (`.fi-company` in tokens.css reads it). A static class cannot hold it.
  return (
    <span
      className="fi-root absolute inset-0"
      data-reduce={reduce ? "true" : "false"}
      style={{ "--fi-accent": accent } as CSSProperties}
    >
      <span
        className={cn(
          "fi-badge",
          placement === "logo" && "fi-ring",
          placement === "name" && "left-[calc(100%_+_12px)] top-1/2 -translate-y-1/2",
          company && "fi-company",
          className,
        )}
        tabIndex={0}
        role="img"
        aria-label={tip}
      >
        {festiveIcon(icon)}
        <span className="fi-tip" aria-hidden="true">{tip}</span>
      </span>
    </span>
  );
}
